import React, { useState, useEffect } from 'react';
import { Loader2, Percent, DollarSign, Tag } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';

const fmt = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);

export default function DiscountModal({ open, onOpenChange, invoice, onSave }) {
  const [type, setType] = useState('percentage');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const originalAmount = parseFloat(invoice?.hours || 0) * parseFloat(invoice?.rate || 195);

  useEffect(() => {
    if (open && invoice) {
      const disc = invoice.discount || {};
      if (disc.type && disc.type !== 'none') {
        setType(disc.type);
        setValue(String(disc.value || ''));
      } else {
        setType('percentage');
        setValue('');
      }
      setReason(invoice.discount_reason_internal || '');
    }
  }, [open, invoice]);

  const numValue = parseFloat(value) || 0;
  const discountAmount = type === 'percentage'
    ? originalAmount * (numValue / 100)
    : numValue;
  const newAmount = Math.max(originalAmount - discountAmount, 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(invoice.id, type, numValue, reason);
      onOpenChange(false);
    } catch {} finally { setSaving(false); }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await onSave(invoice.id, 'none', 0, '');
      onOpenChange(false);
    } catch {} finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-role="heading">
            <Tag className="h-5 w-5 text-primary" />
            Apply Discount
          </DialogTitle>
          <p className="text-xs text-muted-foreground">{invoice?.client || invoice?.task_name}</p>
        </DialogHeader>

        <div className="space-y-4 py-3">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Original Amount</span>
            <span className="font-semibold" data-role="mono">{fmt(originalAmount)}</span>
          </div>

          <div className="flex gap-2">
            <Button variant={type === 'percentage' ? 'default' : 'outline'} size="sm" className="flex-1" onClick={() => setType('percentage')}>
              <Percent className="h-3.5 w-3.5 mr-1" /> Percentage
            </Button>
            <Button variant={type === 'fixed' ? 'default' : 'outline'} size="sm" className="flex-1" onClick={() => setType('fixed')}>
              <DollarSign className="h-3.5 w-3.5 mr-1" /> Fixed
            </Button>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              {type === 'percentage' ? 'Discount (%)' : 'Discount ($)'}
            </label>
            <Input type="text" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)}
              placeholder={type === 'percentage' ? '10' : '50.00'} className="h-9 text-right text-sm" data-role="mono" autoFocus />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Discount Reason (Internal)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Long-standing client, referral credit, billing adjustment..."
              className="w-full text-sm p-2 border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none h-[60px]"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">AI will sanitize this into a professional client-facing note.</p>
          </div>

          {numValue > 0 && (
            <div className="bg-muted rounded-lg p-3 space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Discount</span>
                <span className="text-red-600 font-medium" data-role="mono">-{fmt(discountAmount)}</span>
              </div>
              <div className="flex justify-between text-sm font-semibold border-t pt-1">
                <span>New Amount</span>
                <span className="text-primary" data-role="mono">{fmt(newAmount)}</span>
              </div>
              <p className="text-[10px] text-amber-600 mt-1">
                Discounted invoices are sent as drafts for manual approval.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {invoice?.discount?.type && invoice.discount.type !== 'none' && (
            <Button variant="ghost" size="sm" onClick={handleClear} disabled={saving} className="mr-auto text-muted-foreground">
              Clear
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving || numValue <= 0}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
